import React, {
  useState,
  useRef,
  useCallback,
  useImperativeHandle,
} from "react";
import { Spinner } from "@chakra-ui/react";
import {
  GetDictionaryStatsResponse,
  PutWordStatusRequest,
  UserWord,
  UserWordStatus,
} from "@shared_types";
import { callWithAuth, putWordStatus, deleteUserWord } from "../../api/Api";
import { useQueryClient } from "@tanstack/react-query";
import { useUserWords } from "@/hooks/useUserWords";
import { useDictionaryStats } from "@/hooks/useDictionaryStats";
import { useTrackDictionaryEvents } from "@/hooks/posthog/useTrackDictionaryEvents";
import WordCard from "./WordCard";
import DictionaryEmptyState, { EMPTY_STATES } from "./DictionaryEmptyState";

interface SegmentedWordsViewProps {
  viewMode: "known" | "learning";
  searchQuery: string;
  // should be called on deletes or known / learning status changes
  onStatusUpdate: () => void;
  // reports current known/learning counts up to the shared header
  onCountsChange?: (counts: { known: number; learning: number }) => void;
}

const PAGE_SIZE = 20;

export interface SegmentedWordsViewHandle {
  refreshQueries: () => void;
}

const SegmentedWordsView = React.forwardRef<
  SegmentedWordsViewHandle,
  SegmentedWordsViewProps
>(({ viewMode, searchQuery, onStatusUpdate, onCountsChange }, ref) => {
  const [deleteConfirmId, setDeleteConfirmId] = useState<
    string | number | null
  >(null);
  const [pendingRefetch, setPendingRefetch] = useState(false);
  const [modifiedWords, setModifiedWords] = useState<
    Record<number, UserWordStatus>
  >({});
  // Optimistically removed word ids: hide them immediately on delete, restore on
  // API failure (mirrors iOS handleDeleteWord).
  const [deletedWordIds, setDeletedWordIds] = useState<Set<number>>(new Set());

  const observerRef = useRef<IntersectionObserver | null>(null);
  const lastWordElementRef = useRef<HTMLDivElement | null>(null);
  const queryClient = useQueryClient();
  const { knownWordCount, learningWordCount } = useDictionaryStats();
  const { trackWordMovedToKnown, trackWordMovedToLearning, trackWordDeleted } =
    useTrackDictionaryEvents();

  const {
    activeQuery,
    activeViewWords,
    knownCount,
    learningCount,
    fetchNextPage,
    refetchAllQueries,
    isInitialLoading: isLoading,
    isLoadingMore: isFetchingNextPage,
  } = useUserWords({
    viewMode,
    searchQuery,
    pageSize: PAGE_SIZE,
  });

  React.useEffect(() => {
    onCountsChange?.({ known: knownCount, learning: learningCount });
  }, [knownCount, learningCount, onCountsChange]);

  useImperativeHandle(ref, () => ({
    refreshQueries: () => {
      handleRefetchQueries();
    },
  }));

  const handleRefetchQueries = () => {
    setPendingRefetch(false);
    refetchAllQueries();
    setModifiedWords({});
    setDeletedWordIds(new Set());
  };

  // When the parent switches viewMode, flush any pending refetch.
  React.useEffect(() => {
    if (pendingRefetch) {
      handleRefetchQueries();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode]);

  const lastWordRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (observerRef.current) observerRef.current.disconnect();
      lastWordElementRef.current = node;
      if (!node) return;
      observerRef.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) {
          // loadMore (the hook's fetchNextPage) already gates on displayLimit
          // and hasNextPage/isFetchingNextPage, so a single call is enough.
          fetchNextPage();
        }
      });
      observerRef.current.observe(node);
    },
    [fetchNextPage]
  );

  const handleWordStatusChange = useCallback(async (
    word: UserWord,
    newStatus: UserWordStatus
  ) => {
    const data: PutWordStatusRequest = {
      wordKey: word.wordKey,
      newStatus: newStatus,
      definitionId: word.translationId,
      lessonId: 0,
    };

    try {
      setModifiedWords((prev) => ({
        ...prev,
        [word.userWordId]: newStatus,
      }));
      if (newStatus === "known") {
        trackWordMovedToKnown(
          word.userWordId.toString(),
          word.targetWord,
          "dictionary",
        );
      } else {
        trackWordMovedToLearning(
          word.userWordId.toString(),
          word.targetWord,
          "dictionary",
        );
      }
      await callWithAuth(putWordStatus, data);
      setPendingRefetch(true);
      queryClient.setQueryData(
        ["dictionaryStats"],
        (oldData: GetDictionaryStatsResponse | undefined) => {
        if (!oldData) return oldData;
        const newData = { ...oldData };
        if (newStatus === "known") {
          newData.knownWordCount = (oldData.knownWordCount || 0) + 1;
          newData.learningWordCount = Math.max(
            0,
            (oldData.learningWordCount || 0) - 1
          );
        } else {
          newData.knownWordCount = Math.max(
            0,
            (oldData.knownWordCount || 0) - 1
          );
          newData.learningWordCount = (oldData.learningWordCount || 0) + 1;
        }
        return newData;
      });
      onStatusUpdate();
    } catch (error) {
      // iOS reverts the item to its original status on error (Dictionary.tsx
      // handleStatusChange catch). Drop the optimistic override so the card
      // falls back to word.status.
      setModifiedWords((prev) => {
        const newModified = { ...prev };
        delete newModified[word.userWordId];
        return newModified;
      });
      console.error(error);
    }
  }, [
    queryClient,
    onStatusUpdate,
    trackWordMovedToKnown,
    trackWordMovedToLearning,
  ]);

  const handleDeleteClick = useCallback(async (wordId: number) => {
    if (deleteConfirmId === wordId) {
      const deletedWord = activeViewWords.find((w) => w.userWordId === wordId);
      // iOS removes the word from the list immediately and restores it if the
      // API fails (Dictionary.tsx handleDeleteWord).
      setDeletedWordIds((prev) => new Set(prev).add(wordId));
      setDeleteConfirmId(null);
      try {
        trackWordDeleted(
          wordId.toString(),
          deletedWord?.targetWord ?? "",
          "dictionary",
        );
        await callWithAuth(deleteUserWord, { userWordId: wordId });
        activeQuery.refetch();
        setModifiedWords((prev) => {
          const newModified = { ...prev };
          delete newModified[wordId];
          return newModified;
        });
        onStatusUpdate();
      } catch (error) {
        setDeletedWordIds((prev) => {
          const next = new Set(prev);
          next.delete(wordId);
          return next;
        });
        console.error(error);
      }
    } else {
      setDeleteConfirmId(wordId);
    }
  }, [deleteConfirmId, activeViewWords, activeQuery, onStatusUpdate, trackWordDeleted]);

  const handleCancelDelete = useCallback(() => {
    setDeleteConfirmId(null);
  }, []);

  const deduplicatedWords = deduplicateByUserWordId(activeViewWords).filter(
    (word) => !deletedWordIds.has(word.userWordId)
  );
  const emptyConfig =
    viewMode === "known"
      ? EMPTY_STATES["words-known"]
      : EMPTY_STATES["words-learning"];

  // iOS gates the words empty state on the global learning/known word count
  // (hasLearningWords$ / hasKnownWords$), so a no-result search renders an empty
  // list rather than the first-time onboarding panel (Dictionary.tsx:477-490).
  const hasAnyWords =
    viewMode === "known" ? knownWordCount > 0 : learningWordCount > 0;

  return (
    <div className="min-h-[50vh]">
      {isLoading ? (
        <div className="flex justify-center items-center h-64">
          <Spinner
            size="xl"
            color={
              viewMode === "known"
                ? "var(--color-blue)"
                : "var(--color-orange)"
            }
          />
        </div>
      ) : deduplicatedWords.length === 0 ? (
        hasAnyWords ? null : <DictionaryEmptyState {...emptyConfig} />
      ) : (
        <div className="flex flex-col gap-4">
          {deduplicatedWords.map((word, index) => {
            const effectiveStatus =
              modifiedWords[word.userWordId] || word.status;
            return (
              <WordCard
                viewType={viewMode}
                key={word.userWordId.toString()}
                word={word}
                effectiveStatus={effectiveStatus}
                deleteConfirmId={deleteConfirmId}
                onStatusChange={handleWordStatusChange}
                onDeleteClick={handleDeleteClick}
                onCancelDelete={handleCancelDelete}
                lastWordRef={
                  index === Math.max(0, deduplicatedWords.length - 10)
                    ? lastWordRef
                    : null
                }
              />
            );
          })}

          {isFetchingNextPage && (
            <div className="flex justify-center my-4">
              <Spinner
                size="md"
                color={
                  viewMode === "known"
                    ? "var(--color-blue)"
                    : "var(--color-orange)"
                }
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
});

function deduplicateByUserWordId(words: UserWord[]) {
  const seen: Record<number, boolean> = {};
  return words.filter((word) => {
    if (seen[word.userWordId]) {
      return false;
    }
    seen[word.userWordId] = true;
    return true;
  });
}

export default SegmentedWordsView;
